// ChatPanel: scrollable message list + composer.

import { useEffect, useRef, useState } from "react";
import type { ChatMessageMsg } from "@shared/protocol";

export interface ChatPanelProps {
  messages: ChatMessageMsg[];
  selfClientId: string;
  onSend: (text: string) => void;
}

export function ChatPanel({ messages, selfClientId, onSend }: ChatPanelProps): JSX.Element {
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom only if user is already near the bottom.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };

  return (
    <section className="panel chat">
      <h3>Chat</h3>
      <div className="messages" ref={listRef}>
        {messages.length === 0 && <div className="empty">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.messageId} className={`message${m.sender.clientId === selfClientId ? " self" : ""}`}>
            <div className="meta">
              <span className="name">{m.sender.name}</span>
              <span className="time">{fmtTime(m.sentAt)}</span>
            </div>
            <div className="text">{m.text}</div>
          </div>
        ))}
      </div>
      <div className="composer">
        <input
          type="text"
          maxLength={500}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Say something…"
        />
        <button onClick={submit} disabled={!text.trim()}>Send</button>
      </div>
    </section>
  );
}

function fmtTime(ms: number): string {
  const d = new Date(ms);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}